// Startup playback gate for hls.js sessions.
//
// attemptStartupPlayback holds the first play() until enough media is buffered
// ahead of the resume point, with a hard deadline so a stalled buffer never
// leaves a black screen forever. The deadline used to release the element even
// when nothing at all was buffered yet: on a resume whose first playlist takes
// the server ~9 s (HEVC preroll plus the two-segment ready window) the element
// started "playing" into an empty buffer, and once it was unpaused the startup
// resume seek (#965) was never applied, so audio played the anchor remainder
// under the buffering overlay (pbs_00003789, 2026-09-18). Playing into an empty
// buffer achieves nothing on its own, so the deadline only ever releases an
// element that has media appended.

export type StartupPlaybackGateDecision = "wait" | "play";

export function evaluateStartupPlaybackGate(input: {
  // The element has at least one buffered range.
  hasBufferedMedia: boolean;
  // The startup resume seek is still waiting for its target to be buffered.
  awaitingResumeSeek: boolean;
  bufferedAheadSeconds: number;
  minBufferSeconds: number;
  nowMs: number;
  deadlineMs: number;
  // Native HLS resume after a session switch keeps its own readiness rule.
  resumeNativeSessionSwitch: boolean;
}): StartupPlaybackGateDecision {
  const {
    hasBufferedMedia,
    awaitingResumeSeek,
    bufferedAheadSeconds,
    minBufferSeconds,
    nowMs,
    deadlineMs,
    resumeNativeSessionSwitch,
  } = input;
  if (resumeNativeSessionSwitch) {
    return "play";
  }
  if (!hasBufferedMedia) {
    return "wait";
  }
  if ((awaitingResumeSeek || bufferedAheadSeconds < minBufferSeconds) && nowMs < deadlineMs) {
    return "wait";
  }
  return "play";
}
